import type { Circular, CircularCategory } from './types';

export const CATEGORY_LABEL: Record<CircularCategory, string> = {
  examination: 'Examination',
  fees: 'Fees',
  governance: 'Governance',
  certificate: 'Certificate',
  office: 'Office',
  general: 'General',
};

// Order the categories appear in the Notices page filter.
const CATEGORY_ORDER: CircularCategory[] = ['examination', 'fees', 'governance', 'certificate', 'office', 'general'];

/** The categories that actually have a notice, in a fixed order. Staff never manage this list. */
export function categoriesPresent(items: Circular[]): CircularCategory[] {
  const present = new Set(items.map((c) => c.category));
  return CATEGORY_ORDER.filter((c) => present.has(c));
}

// Home keeps four coarse filters; "General" is everything that is not
// Examination or Fees. The Notices page uses the real categories instead.
export const FILTERS = ['All', 'Examination', 'Fees', 'General'] as const;
export type CircularFilter = (typeof FILTERS)[number];

export function matchesFilter(c: Circular, filter: CircularFilter): boolean {
  if (filter === 'All') return true;
  if (filter === 'Examination') return c.category === 'examination';
  if (filter === 'Fees') return c.category === 'fees';
  return c.category !== 'examination' && c.category !== 'fees';
}

export function newestFirst(items: Circular[]): Circular[] {
  return [...items].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
}

/** Newest-first list split by calendar year: [{ year: '2026', items }, ...]. */
export function groupByYear(items: Circular[]): { year: string; items: Circular[] }[] {
  const groups: { year: string; items: Circular[] }[] = [];
  for (const c of items) {
    const year = c.publishedAt.slice(0, 4);
    const last = groups[groups.length - 1];
    if (last && last.year === year) last.items.push(c);
    else groups.push({ year, items: [c] });
  }
  return groups;
}

// The Examinations page card shows the pinned circular, otherwise the newest
// examination one. `items` must already be newest first.
export function pickLatest(items: Circular[]): Circular | undefined {
  return items.find((c) => c.isPinned) ?? items.find((c) => c.category === 'examination');
}
